import { useMutation } from '@tanstack/react-query'
import { createFileRoute, Link, useRouter } from '@tanstack/react-router'
import { App, Button, Form, Input } from 'antd'
import { createSchemaFieldRule } from 'antd-zod'
import { BadgeCheck, LockKeyhole, Mail } from 'lucide-react'
import {
  resetPasswordSchema,
  sendPasswordResetVerificationSchema,
  type ResetPasswordInput
} from '@super-mega/server/contracts/auth'
import {
  AUTH_FIELD_CLASS_NAME,
  AUTH_FORM_CLASS_NAMES,
  AUTH_PAIRED_FIELD_CLASS_NAME,
  AuthFormHeader,
  AuthShell,
  getAuthError
} from '@/features/auth/index.ts'
import { useTRPC } from '@/trpc/react.ts'

const resetPasswordRule = createSchemaFieldRule(resetPasswordSchema)
const sendVerificationRule = createSchemaFieldRule(sendPasswordResetVerificationSchema)
const resetPasswordToastKey = 'auth-reset-password'
const verificationToastKey = 'auth-reset-password-verification'

export const Route = createFileRoute('/auth/forgot-password')({
  component: ForgotPasswordPage,
  head: () => ({
    meta: [{ title: '重置密码 · Super Mega 租赁管理一体化智能系统' }]
  })
})

function ForgotPasswordPage() {
  const { redirect: redirectAfterLogin } = Route.useSearch()
  const trpc = useTRPC()
  const router = useRouter()
  const { message: messageApi } = App.useApp()
  const [form] = Form.useForm<ResetPasswordInput>()
  const sendVerification = useMutation(trpc.auth.sendPasswordResetVerification.mutationOptions())
  const resetPassword = useMutation(trpc.auth.resetPassword.mutationOptions())

  const handleSendVerification = async () => {
    let email: string
    try {
      const values = await form.validateFields(['email'])
      email = values.email
    } catch {
      return
    }

    messageApi.destroy(verificationToastKey)
    try {
      await sendVerification.mutateAsync({ email })
      void messageApi.open({
        content: '如果该邮箱已注册，密码重置验证码将发送到你的邮箱',
        duration: 4,
        key: verificationToastKey,
        type: 'success'
      })
    } catch (mutationError) {
      const presentation = getAuthError(mutationError, '发送验证码')
      void messageApi.open({
        content: presentation.message,
        duration: 3,
        key: verificationToastKey,
        type: 'error'
      })
    }
  }

  const handleSubmit = async (values: ResetPasswordInput) => {
    messageApi.destroy(resetPasswordToastKey)
    try {
      await resetPassword.mutateAsync(values)
      void messageApi.open({
        content: '密码已重置，请使用新密码登录',
        duration: 3,
        key: resetPasswordToastKey,
        type: 'success'
      })
      await router.navigate({
        search: { redirect: redirectAfterLogin },
        to: '/auth/signin',
        replace: true
      })
    } catch (mutationError) {
      const presentation = getAuthError(mutationError, '重置密码')
      void messageApi.open({
        content: presentation.message,
        duration: 3,
        key: resetPasswordToastKey,
        type: 'error'
      })
    }
  }

  return (
    <AuthShell>
      <AuthFormHeader description="输入登录邮箱获取验证码，然后设置新密码。" title="重置密码" />

      <Form<ResetPasswordInput>
        classNames={AUTH_FORM_CLASS_NAMES}
        disabled={resetPassword.isPending || sendVerification.isPending}
        form={form}
        initialValues={{ confirmPassword: '', email: '', password: '', verificationCode: '' }}
        layout="vertical"
        onFinish={(values) => void handleSubmit(values)}
        requiredMark={false}
        scrollToFirstError={{ block: 'nearest', scrollMode: 'if-needed', focus: true }}
        size="large"
      >
        <Form.Item<ResetPasswordInput>
          className={AUTH_FIELD_CLASS_NAME}
          label="登录邮箱"
          name="email"
          rules={[sendVerificationRule]}
        >
          <Input
            allowClear
            autoComplete="email"
            autoFocus
            autoCapitalize="none"
            maxLength={320}
            placeholder="请输入登录邮箱"
            prefix={<Mail aria-hidden className="size-4" strokeWidth={1.8} />}
            spellCheck={false}
          />
        </Form.Item>

        <Form.Item<ResetPasswordInput>
          className={AUTH_FIELD_CLASS_NAME}
          label="邮箱验证码"
          name="verificationCode"
          rules={[resetPasswordRule]}
        >
          <Input
            autoCapitalize="none"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="6 位验证码"
            prefix={<BadgeCheck aria-hidden className="size-4" strokeWidth={1.8} />}
            spellCheck={false}
            suffix={
              <span className="border-border inline-flex items-center border-l pl-2">
                <Button
                  className="px-1! text-xs!"
                  htmlType="button"
                  loading={sendVerification.isPending}
                  onClick={() => void handleSendVerification()}
                  size="small"
                  type="link"
                >
                  获取验证码
                </Button>
              </span>
            }
          />
        </Form.Item>

        <div className="grid grid-cols-2 gap-x-3 sm:gap-x-4">
          <Form.Item<ResetPasswordInput>
            className={AUTH_PAIRED_FIELD_CLASS_NAME}
            label="新密码"
            name="password"
            rules={[resetPasswordRule]}
          >
            <Input.Password
              autoComplete="new-password"
              maxLength={1024}
              placeholder="至少 8 位"
              prefix={<LockKeyhole aria-hidden className="size-4" strokeWidth={1.8} />}
            />
          </Form.Item>

          <Form.Item<ResetPasswordInput>
            className={AUTH_PAIRED_FIELD_CLASS_NAME}
            dependencies={['password']}
            label="确认新密码"
            name="confirmPassword"
            rules={[resetPasswordRule]}
          >
            <Input.Password
              autoComplete="new-password"
              maxLength={1024}
              placeholder="再次输入新密码"
              prefix={<LockKeyhole aria-hidden className="size-4" strokeWidth={1.8} />}
            />
          </Form.Item>
        </div>

        <Button
          block
          className="h-12! font-semibold!"
          htmlType="submit"
          loading={resetPassword.isPending}
          type="primary"
        >
          确认重置密码
        </Button>
      </Form>

      <div className="mt-4 text-center text-sm text-black/45 dark:text-white/45">
        想起密码了？
        <Link
          className="ml-1 font-medium !text-[var(--primary)] hover:underline"
          search={{ redirect: redirectAfterLogin }}
          to="/auth/signin"
        >
          返回登录
        </Link>
      </div>
    </AuthShell>
  )
}
